/**
 * Which page the console shows, kept in the URL's hash: `#/credentials/polygon`.
 *
 * The hash rather than the path, because the console is a static bundle served from
 * wherever it is put — a path route would need every host to send unknown paths back to
 * `index.html`, and the hash needs nothing of it. The query string stays the pages' own:
 * `?backend=` chooses a service and `?symbol=` a chain, and a route change leaves both alone.
 */
export type Route = 'accounts' | 'credit-spreads' | 'credentials' | 'notifiers';

export interface RouteInfo {
  readonly route: Route;
  readonly label: string;
  readonly hash: string;
}

/** In the order the navigation shows them. The first is where an empty hash lands. */
export const ROUTES: ReadonlyArray<RouteInfo> = [
  { route: 'accounts', label: 'Accounts', hash: '#/accounts' },
  { route: 'credit-spreads', label: 'Credit spreads', hash: '#/credit-spreads' },
  { route: 'credentials', label: 'Credentials', hash: '#/credentials' },
  { route: 'notifiers', label: 'Notifiers', hash: '#/notifiers' },
];

/** Names a page used to have, so a bookmark to one still opens what replaced it. */
const RENAMED: Readonly<Record<string, Route>> = { settings: 'credentials' };

export interface NavSection {
  readonly title: string;
  readonly routes: ReadonlyArray<Route>;
}

/** How the sidebar groups the pages, as mini-cloud's does: the ledger's own first. */
export const NAV_SECTIONS: ReadonlyArray<NavSection> = [
  { title: 'Ledger', routes: ['accounts'] },
  { title: 'Research', routes: ['credit-spreads'] },
  { title: 'Configure', routes: ['credentials', 'notifiers'] },
];

function segments(hash: string): ReadonlyArray<string> {
  return hash.replace(/^#\/?/, '').split('?')[0].split('/');
}

/** The route a hash names. Anything unrecognised is the first page, not an error page: a stale bookmark should still open the console. */
export function parseRoute(hash: string): Route {
  const name = segments(hash)[0];
  return ROUTES.find((entry) => entry.route === name)?.route ?? RENAMED[name] ?? ROUTES[0].route;
}

export function routeInfo(route: Route): RouteInfo {
  return ROUTES.find((entry) => entry.route === route) ?? ROUTES[0];
}

/** The credentials page's tabs, one per kind of key. */
export type CredentialTab = 'alpaca' | 'polygon';
export const CREDENTIAL_TABS: ReadonlyArray<{ readonly tab: CredentialTab; readonly label: string; readonly hash: string }> = [
  { tab: 'alpaca', label: 'Alpaca', hash: '#/credentials/alpaca' },
  { tab: 'polygon', label: 'Polygon', hash: '#/credentials/polygon' },
];

/** The tab a credentials hash names, Alpaca when it names none. */
export function parseCredentialTab(hash: string): CredentialTab {
  const tab = segments(hash)[1];
  return CREDENTIAL_TABS.find((entry) => entry.tab === tab)?.tab ?? 'alpaca';
}

/**
 * The account an accounts hash names — `#/accounts/MOMENTUM01` — or undefined for the
 * listing. Encoded in the hash, since an id is whatever its creator typed.
 */
export function parseAccountId(hash: string): string | undefined {
  const id = segments(hash)[1];
  if (id === undefined || id === '') {
    return undefined;
  }
  try {
    return decodeURIComponent(id);
  } catch {
    // A malformed escape is a mangled link, not an id; the listing is where to pick one.
    return undefined;
  }
}

export function accountHash(accountId: string): string {
  return `#/accounts/${encodeURIComponent(accountId)}`;
}
